import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDraggable } from "@dnd-kit/react";
import {
  formatCustomUnitMeasurementEquivalenceSuffix,
  parseUnitToShow,
} from "../../../../../utils/nutrition-util";
import { MacrosDisplay } from "./MacrosDisplay";
import { CopyComponent } from "../../../../../parts/CopyComponent";
import {
  getCustomMealDraggableId,
  getCustomMealDragData,
} from "../utils/meal-drag-drop-helpers";

/**
 * GroupedMealContainer
 * 
 * Component for displaying a grouped/custom meal with drag and drop functionality
 */
export const GroupedMealContainer = ({
  group,
  mealType,
  day,
  planMode,
  week,
  viewMode,
  query,
  hideHeaderActions = false,
}) => {
  const { t } = useTranslation("athlete-portal");
  const { meal, foods: foodsOfMeal, totals } = group;
  const {
    name: mealname,
    description: mealDescription,
    _id: mealId,
  } = meal;
  const {
    calories = 0,
    carbs = 0,
    fats = 0,
    proteins = 0,
  } = totals;

  const currentPlanMode = planMode || "detailed";

  // Custom meal draggable hook (disabled when only item in slot to avoid duplicate UI)
  const isCustomMealDraggable = !viewMode && !hideHeaderActions && mealId && foodsOfMeal?.length > 0;
  const {
    ref: refCustomMealDraggable,
    handleRef: handleRefCustomMealDraggable,
    attributes: customMealAttributes,
    listeners: customMealListeners,
    isDragging: isCustomMealDragging,
  } = useDraggable({
    id: isCustomMealDraggable
      ? getCustomMealDraggableId(currentPlanMode, day, mealType, mealId)
      : `custom-meal-static-${mealId ?? "nodrag"}`,
    disabled: !isCustomMealDraggable,
    data: isCustomMealDraggable
      ? getCustomMealDragData(currentPlanMode, day, mealType, meal, foodsOfMeal, week)
      : undefined,
  });

  return (
    <div 
      key={mealId} 
      className={`grouped-meal full-info ${isCustomMealDragging ? 'pointer-events-none opacity-0' : ''}`}
      ref={isCustomMealDraggable ? refCustomMealDraggable : null}
      role={isCustomMealDraggable ? undefined : "presentation"}
      tabIndex={isCustomMealDraggable ? undefined : -1}
    >
      {/* Meal Header */}
      <div className="grouped-meal-header">
        <div className="grouped-meal-header-content">
          <span className="title">{mealname}</span>
          <span className="subtitle pb-1.5">{mealDescription}</span>
        </div>
        {!viewMode && !hideHeaderActions && (
          <div className={`header-actions mobile-hidden ${isCustomMealDragging ? 'hidden!' : ''}`}>
            <CopyComponent
              type="meal"
              meal={{ ...meal, foods: foodsOfMeal }}
              day={day}
              query={query}
              planMode={currentPlanMode}
            />
            <div
              className="drag-icon cursor-grab"
              ref={handleRefCustomMealDraggable}
              {...customMealAttributes}
              {...customMealListeners}
              role="div"
            >
              <img
                src="/img/new-icons/drag-outline.svg"
                alt={t("foodItem.dragAlt")}
              />
            </div>
          </div>
        )}
      </div>
      {/* Foods inside this meal */}
      {foodsOfMeal.map((food, index) => {
        const {
          type: foodType,
          quantityNumber,
          unitOfMeasure,
          foodId,
        } = food;
        if (!foodId) return null;

        const { name, quantityDescription, brand } = foodId;

        return (
          <div
            className="meal-foods"
            key={`${mealId}-food-${index}`}
          >
            <div className="meal-foods-info">
              <div className="meal-foods-info-food">
                <div className="meal-foods-info-title">
                  {name}
                  {brand && !name.toLowerCase().includes(brand.toLowerCase())
                    ? ` (${brand})`
                    : ""}
                </div>
                <div className="meal-foods-info-subtitle">
                  {quantityNumber}{" "}
                  {parseUnitToShow(
                    quantityNumber,
                    foodType,
                    unitOfMeasure,
                    quantityDescription
                  )}
                  {formatCustomUnitMeasurementEquivalenceSuffix(
                    quantityNumber,
                    unitOfMeasure,
                    foodId
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
      <MacrosDisplay
        proteins={proteins}
        carbs={carbs}
        fats={fats}
        calories={calories}
        className="values meal-values"
      />
    </div>
  );
};

GroupedMealContainer.propTypes = {
  group: PropTypes.object.isRequired,
  mealType: PropTypes.string.isRequired,
  day: PropTypes.object.isRequired,
  planMode: PropTypes.string,
  week: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  viewMode: PropTypes.bool,
  query: PropTypes.object,
  hideHeaderActions: PropTypes.bool,
};
